import { reactive } from 'vue';
import CloseCircleMembersAPI from 'dashboard/api/closeCircleMembers';
import {
  CLOSE_CIRCLE_DATE_TYPES,
  CLOSE_CIRCLE_RELATIONSHIPS,
} from './constants.js';

const state = reactive({
  membersByContactId: {},
  loadingByContactId: {},
  loadedByContactId: {},
  errorByContactId: {},
});

const normalizeContactId = contactId => String(contactId ?? '');

const normalizeTimestamp = value => {
  if (!value) return '';
  if (typeof value === 'number') return new Date(value * 1000).toISOString();
  return value;
};

const normalizeImportantDatePayload = date => ({
  id: date.id,
  type: date.type || 'other',
  label: date.label?.trim() ?? '',
  date: date.date || '',
});

const buildImportantDates = payload => {
  const existing =
    payload.importantDates
      ?.map(normalizeImportantDatePayload)
      .filter(date => date.date) ?? [];
  if (existing.length) return existing;

  const timestamp = Date.now();
  const dates = [];
  if (payload.birthday) {
    dates.push({
      id: `date_${timestamp}_birthday`,
      type: 'birthday',
      label: '',
      date: payload.birthday,
    });
  }
  if (payload.importantDate) {
    dates.push({
      id: `date_${timestamp}_important`,
      type: payload.importantDateType || 'other',
      label: payload.importantDateLabel || '',
      date: payload.importantDate,
    });
  }
  return dates;
};

const normalizeMemberPayload = (payload, contactId) => {
  const importantDates = buildImportantDates(payload);
  const birthday =
    payload.birthday ||
    importantDates.find(date => date.type === 'birthday')?.date ||
    (payload.importantDateType === 'birthday' ? payload.importantDate : '') ||
    '';

  return {
    contactId: normalizeContactId(contactId ?? payload.contactId),
    firstName: payload.firstName?.trim() ?? '',
    lastName: payload.lastName?.trim() ?? '',
    relationship: payload.relationship || 'other',
    birthday,
    email: payload.email?.trim() ?? '',
    phone: payload.phone?.trim() ?? '',
    preferredContactChannel: payload.preferredContactChannel || null,
    preferredLanguage: payload.preferredLanguage || null,
    canBeContactedDirectly: Boolean(payload.canBeContactedDirectly),
    importantDates,
    contactPreferences: payload.contactPreferences ?? {},
    notes: payload.notes?.trim() ?? '',
  };
};

const toApiPayload = payload => {
  const normalized = normalizeMemberPayload(payload, payload.contactId);
  return {
    first_name: normalized.firstName,
    last_name: normalized.lastName,
    relationship: normalized.relationship,
    birthday: normalized.birthday || null,
    email: normalized.email || null,
    phone: normalized.phone || null,
    preferred_contact_channel: normalized.preferredContactChannel,
    preferred_language: normalized.preferredLanguage,
    can_be_contacted_directly: normalized.canBeContactedDirectly,
    important_dates: normalized.importantDates,
    contact_preferences: normalized.contactPreferences,
    notes: normalized.notes,
  };
};

const normalizeImportantDates = member =>
  (member.important_dates ?? []).map((date, index) => ({
    ...date,
    id: date.id ?? `date_${member.id}_${index}`,
    label: date.label?.trim() ?? '',
  }));

const fromApiPayload = member => ({
  id: member.id,
  contactId: normalizeContactId(member.contact_id),
  firstName: member.first_name ?? '',
  lastName: member.last_name ?? '',
  relationship: member.relationship ?? 'other',
  birthday: member.birthday ?? '',
  email: member.email ?? '',
  phone: member.phone ?? '',
  preferredContactChannel: member.preferred_contact_channel ?? null,
  preferredLanguage: member.preferred_language ?? null,
  canBeContactedDirectly: Boolean(member.can_be_contacted_directly),
  importantDates: normalizeImportantDates(member),
  contactPreferences: member.contact_preferences ?? [],
  notes: member.notes ?? '',
  createdAt: normalizeTimestamp(member.created_at),
  updatedAt: normalizeTimestamp(member.updated_at),
});

const membersForNormalizedContact = contactId =>
  state.membersByContactId[contactId] ?? [];

const setMembersForContact = (contactId, members) => {
  state.membersByContactId[contactId] = members;
};

const upsertById = (contactId, member) => {
  const members = membersForNormalizedContact(contactId);
  const index = members.findIndex(
    item => String(item.id) === String(member.id)
  );
  const nextMembers =
    index === -1
      ? [member, ...members]
      : members.map(item =>
          String(item.id) === String(member.id) ? member : item
        );

  setMembersForContact(contactId, nextMembers);
};

export const useCloseCircleState = () => {
  const membersForContact = contactId => {
    const normalized = normalizeContactId(contactId);
    return membersForNormalizedContact(normalized);
  };

  const findMember = memberId =>
    Object.values(state.membersByContactId)
      .flat()
      .find(member => String(member.id) === String(memberId));

  const loadMembers = async (contactId, { force = false } = {}) => {
    const normalized = normalizeContactId(contactId);
    if (!normalized) return [];
    if (!force && state.loadedByContactId[normalized]) {
      return membersForNormalizedContact(normalized);
    }
    if (state.loadingByContactId[normalized]) {
      return membersForNormalizedContact(normalized);
    }

    state.loadingByContactId[normalized] = true;
    state.errorByContactId[normalized] = null;

    try {
      const { data } = await CloseCircleMembersAPI.get(normalized);
      const members = data.map(fromApiPayload);
      setMembersForContact(normalized, members);
      state.loadedByContactId[normalized] = true;
      return members;
    } catch (error) {
      state.errorByContactId[normalized] = error;
      return membersForNormalizedContact(normalized);
    } finally {
      state.loadingByContactId[normalized] = false;
    }
  };

  const memberName = member =>
    [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim();

  const relationshipConfig = value =>
    CLOSE_CIRCLE_RELATIONSHIPS.find(item => item.value === value) ??
    CLOSE_CIRCLE_RELATIONSHIPS.at(-1);

  const dateTypeConfig = value =>
    CLOSE_CIRCLE_DATE_TYPES.find(item => item.value === value) ??
    CLOSE_CIRCLE_DATE_TYPES.at(-1);

  const createMember = async (contactId, payload) => {
    const normalized = normalizeContactId(contactId);
    const { data } = await CloseCircleMembersAPI.create(
      normalized,
      toApiPayload({ ...payload, contactId: normalized })
    );
    const member = fromApiPayload(data);
    upsertById(normalized, member);
    return member;
  };

  const updateMember = async (memberId, payload) => {
    const existing = findMember(memberId);
    if (!existing) return null;
    const normalized = normalizeContactId(existing.contactId);
    const { data } = await CloseCircleMembersAPI.update(
      normalized,
      memberId,
      toApiPayload({
        ...payload,
        contactId: normalized,
        importantDates:
          payload.importantDates ??
          (payload.importantDate ? undefined : existing.importantDates),
      })
    );
    const member = fromApiPayload(data);
    upsertById(normalized, member);
    return member;
  };

  const detachMember = async memberId => {
    const existing = findMember(memberId);
    if (!existing) return;
    const normalized = normalizeContactId(existing.contactId);
    await CloseCircleMembersAPI.delete(normalized, memberId);
    setMembersForContact(
      normalized,
      membersForNormalizedContact(normalized).filter(
        member => String(member.id) !== String(memberId)
      )
    );
  };

  const beneficiarySnapshot = member => ({
    firstName: member?.firstName ?? '',
    lastName: member?.lastName ?? '',
    relationship: member?.relationship ?? 'other',
  });

  const isLoading = contactId =>
    Boolean(state.loadingByContactId[normalizeContactId(contactId)]);

  const errorForContact = contactId =>
    state.errorByContactId[normalizeContactId(contactId)] ?? null;

  return {
    state,
    relationships: CLOSE_CIRCLE_RELATIONSHIPS,
    dateTypes: CLOSE_CIRCLE_DATE_TYPES,
    loadMembers,
    isLoading,
    errorForContact,
    membersForContact,
    findMember,
    memberName,
    relationshipConfig,
    dateTypeConfig,
    createMember,
    updateMember,
    detachMember,
    beneficiarySnapshot,
  };
};
